import type { Frequency, RuleInput } from "@app/core";
import type { I18nKey } from "../../../lib/language.js";
import { useI18n } from "../../../lib/useI18n.js";
import { Check, MiniStepper } from "./bits.js";
import { DateChip } from "./DateTimeChips.js";
import { isoDateOffset } from "./palette.js";

interface Props {
  open: boolean;
  rule: RuleInput | null;
  onRule: (next: RuleInput) => void;
  untilStr: string;
  onUntil: (dateStr: string) => void;
}

const FREQS: Array<{ id: Frequency; labelKey: I18nKey; unit: "day" | "week" | "month" | "year" }> = [
  { id: "daily", labelKey: "repeat.daily", unit: "day" },
  { id: "weekly", labelKey: "repeat.weekly", unit: "week" },
  { id: "monthly", labelKey: "repeat.monthly", unit: "month" },
  { id: "yearly", labelKey: "repeat.yearly", unit: "year" },
];

type Weekday = NonNullable<RuleInput["byDay"]>[number];
const WEEKDAYS: Array<{ key: Weekday; label: string }> = [
  { key: "MO", label: "M" }, { key: "TU", label: "T" }, { key: "WE", label: "W" },
  { key: "TH", label: "T" }, { key: "FR", label: "F" }, { key: "SA", label: "S" }, { key: "SU", label: "S" },
];

/** Repeat / series panel. Slides open when "Repeating" is checked: frequency +
 *  interval stepper + weekly byDay chips + optional series end date. */
export function RepeatPanel({ open, rule, onRule, untilStr, onUntil }: Props) {
  const { t } = useI18n();
  const r: RuleInput = rule ?? { freq: "weekly", interval: 1 };
  const every = r.interval ?? 1;
  const unit = FREQS.find((f) => f.id === r.freq)?.unit ?? "week";
  const plural = t(`repeat.${every > 1 ? `${unit}s` : unit}` as I18nKey);
  const endOn = !!untilStr;

  const bump = (d: number) => onRule({ ...r, interval: Math.min(99, Math.max(1, every + d)) });

  const toggleWeekday = (day: Weekday) => {
    const current = r.byDay ?? [];
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    const { byDay: _drop, ...rest } = r;
    onRule(next.length ? { ...rest, byDay: next } : rest);
  };

  return (
    <div className={"repeat-panel" + (open ? " is-open" : "")} aria-hidden={!open}>
      <div className="repeat-inner">
        <div className="repeat-card">
          <div className="field">
            <div className="field-label">{t("repeat.frequency")}</div>
            <div className="freq-seg" role="tablist">
              {FREQS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  className={r.freq === f.id ? "is-on" : ""}
                  onClick={() => onRule({ ...r, freq: f.id })}
                >{t(f.labelKey)}</button>
              ))}
            </div>
          </div>

          {r.freq === "weekly" && (
            <div className="field">
              <div className="field-label">{t("repeat.onDays")}</div>
              <div className="weekday-row">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    className={(r.byDay ?? []).includes(d.key) ? "is-on" : ""}
                    onClick={() => toggleWeekday(d.key)}
                    aria-label={d.key}
                  >{d.label}</button>
                ))}
              </div>
            </div>
          )}

          <div className="repeat-grid">
            <div className="field">
              <div className="field-label">{t("repeat.every")}</div>
              <div className="every-stepper">
                <MiniStepper value={every} onStep={bump} />
                <span className="every-unit">{plural}</span>
              </div>
            </div>

            <div className="field end-field">
              <Check
                checked={endOn}
                onChange={(v) => onUntil(v ? (untilStr || isoDateOffset(60)) : "")}
              >
                {t("repeat.endDate")}
              </Check>
              <DateChip
                value={untilStr}
                onChange={onUntil}
                ariaLabel={t("repeat.seriesEndDate")}
                chipBase="date-chip"
                disabled={!endOn}
                placeholder={t("repeat.noEnd")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
